/**
 * CommentItem.jsx
 * Sprint 4 – Enterprise Comments
 *
 * Renders one comment (top-level or reply) with:
 *   - Avatar (initials + role colour)
 *   - Name, role badge, timestamp, "Edited" label
 *   - Subtask badge (if subtask_id is set)
 *   - @mention highlights inside message text
 *   - Reply thread (nested, indented)
 *   - Edit / Delete controls (sender only, task-open only)
 *   - Reply button (all authorised users, task-open only)
 *   - Search highlight support
 */
import React, { useState } from 'react';
import CommentInput from './CommentInput';

// ── Helpers ───────────────────────────────────────────────────────────────────

function initials(name = '') {
  return name.split(' ').map(p => p[0] || '').join('').toUpperCase().slice(0, 2);
}

const ROLE_BADGE = {
  Manager:   'bg-purple-100 text-purple-700',
  admin:     'bg-red-100 text-red-700',
  superuser: 'bg-orange-100 text-orange-700',
  employee:  'bg-blue-100 text-blue-700',
};
const AVATAR_COLOR = {
  Manager:   'bg-purple-200 text-purple-800',
  admin:     'bg-red-200 text-red-800',
  superuser: 'bg-orange-200 text-orange-800',
  employee:  'bg-blue-200 text-blue-800',
};
function roleBadge(role) { return ROLE_BADGE[role] || 'bg-gray-100 text-gray-600'; }
function avatarBg(role)  { return AVATAR_COLOR[role] || 'bg-gray-200 text-gray-700'; }

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleString('en-IN', {
    day:    '2-digit',
    month:  'short',
    year:   'numeric',
    hour:   '2-digit',
    minute: '2-digit',
  });
}

/**
 * Highlight @mentions and search terms inside comment text.
 * Returns an array of React nodes.
 */
function renderMessage(text, members = [], searchQuery = '') {
  if (!text) return null;

  // Build mention regex from member names
  const mentionNames = members
    .map(m => `${m.first_name} ${m.last_name}`)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length); // longest first to avoid partial matches

  const parts = [];
  let remaining = text;

  // Simple sequential scan: find @Name or search term
  const escapeRx = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const mentionRxStr = mentionNames.length
    ? `(@(?:${mentionNames.map(escapeRx).join('|')}))` : null;
  const searchRxStr  = searchQuery?.trim()
    ? `(${escapeRx(searchQuery.trim())})` : null;

  const combined = [mentionRxStr, searchRxStr].filter(Boolean).join('|');
  if (!combined) return <span className="whitespace-pre-wrap break-words">{text}</span>;

  const rx = new RegExp(combined, 'gi');
  let lastIdx = 0;
  let match;
  let key = 0;

  while ((match = rx.exec(text)) !== null) {
    if (match.index > lastIdx) {
      parts.push(<span key={key++}>{text.slice(lastIdx, match.index)}</span>);
    }
    const chunk = match[0];
    if (chunk.startsWith('@')) {
      parts.push(
        <span key={key++} className="font-semibold text-[#4a7a20] bg-[#edf5e0] rounded px-0.5">
          {chunk}
        </span>
      );
    } else {
      parts.push(
        <mark key={key++} className="bg-yellow-200 text-yellow-900 rounded px-0.5">
          {chunk}
        </mark>
      );
    }
    lastIdx = match.index + chunk.length;
  }
  if (lastIdx < text.length) parts.push(<span key={key++}>{text.slice(lastIdx)}</span>);

  return <span className="whitespace-pre-wrap break-words">{parts}</span>;
}

// ── CommentItem ───────────────────────────────────────────────────────────────

export default function CommentItem({
  comment,
  currentUserId,
  taskReadOnly = false,
  members      = [],
  subtasks     = [],
  searchQuery  = '',
  onReply,     // (parentId, { message, mentions }) => Promise
  onEdit,      // (commentId, newMessage) => Promise
  onDelete,    // (commentId) => Promise
  depth        = 0,   // 0 = top-level, 1 = reply (max indent)
}) {
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const [editMode, setEditMode]         = useState(false);
  const [editText, setEditText]         = useState(comment.message);
  const [savingEdit, setSavingEdit]     = useState(false);
  const [deleting, setDeleting]         = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isMine     = Number(comment.author_id) === Number(currentUserId);
  const canWrite   = !taskReadOnly;
  const isDeleted  = comment.is_deleted;

  // ── Edit handlers ──────────────────────────────────────────────────────────
  const handleSaveEdit = async () => {
    if (!editText.trim() || !onEdit) return;
    setSavingEdit(true);
    try {
      await onEdit(comment.id, editText.trim());
      setEditMode(false);
    } finally {
      setSavingEdit(false);
    }
  };

  // ── Delete handlers ────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!onDelete) return;
    setDeleting(true);
    try {
      await onDelete(comment.id);
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  // ── Reply handler ──────────────────────────────────────────────────────────
  const handleSendReply = async (payload) => {
    if (!onReply) return;
    setSendingReply(true);
    try {
      await onReply(comment.id, payload);
      setShowReplyBox(false);
    } finally {
      setSendingReply(false);
    }
  };

  const replies = comment.replies || [];

  return (
    <div className={`flex gap-3 ${depth > 0 ? 'pl-10 mt-2' : 'mt-4'}`}>
      {/* ── Avatar ── */}
      <div className="flex-shrink-0">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold select-none ${avatarBg(comment.author_role)}`}>
          {initials(comment.author_name)}
        </div>
      </div>

      {/* ── Bubble ── */}
      <div className="flex-1 min-w-0">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mb-1">
          <span className="text-sm font-semibold text-gray-900">
            {comment.author_name || 'Unknown'}
          </span>
          {comment.author_role && (
            <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${roleBadge(comment.author_role)}`}>
              {comment.author_role}
            </span>
          )}
          <span className="text-xs text-gray-400">{formatTime(comment.created_at)}</span>
          {comment.is_edited && (
            <span className="text-xs text-gray-400 italic">(edited)</span>
          )}
        </div>

        {/* Subtask badge */}
        {comment.subtask_title && (
          <div className="mb-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 inline-block" />
            Subtask: {comment.subtask_title}
          </div>
        )}

        {/* Message body */}
        <div className={`rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${
          isDeleted
            ? 'bg-gray-50 text-gray-400 italic border border-dashed border-gray-200'
            : isMine
            ? 'bg-[#f0f7e8] text-gray-800'
            : 'bg-gray-100 text-gray-800'
        }`}>
          {isDeleted ? (
            <span>This comment was deleted.</span>
          ) : editMode ? (
            <div className="flex flex-col gap-2">
              <textarea
                value={editText}
                onChange={e => setEditText(e.target.value)}
                rows={3}
                autoFocus
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] resize-none"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSaveEdit}
                  disabled={savingEdit || !editText.trim()}
                  className="px-3 py-1.5 rounded-lg bg-[#7AA33E] text-white text-xs font-semibold disabled:opacity-50 hover:bg-[#6a9335] transition"
                >
                  {savingEdit ? 'Saving…' : 'Save'}
                </button>
                <button
                  onClick={() => { setEditMode(false); setEditText(comment.message); }}
                  className="px-3 py-1.5 rounded-lg bg-gray-200 text-gray-700 text-xs font-semibold hover:bg-gray-300 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            renderMessage(comment.message, members, searchQuery)
          )}
        </div>

        {/* Actions row */}
        {!isDeleted && (
          <div className="mt-1.5 flex items-center gap-3 flex-wrap">
            {/* Reply — available to all authorised users while task is open */}
            {canWrite && depth === 0 && (
              <button
                onClick={() => setShowReplyBox(v => !v)}
                className="text-xs text-gray-400 hover:text-[#7AA33E] font-medium transition"
              >
                {showReplyBox ? 'Cancel reply' : `↩ Reply${replies.length > 0 ? ` (${replies.length})` : ''}`}
              </button>
            )}
            {/* Reply count badge (read-only mode) */}
            {(!canWrite || depth !== 0) && replies.length > 0 && (
              <span className="text-xs text-gray-400">{replies.length} {replies.length === 1 ? 'reply' : 'replies'}</span>
            )}

            {/* Edit — sender only, task open */}
            {isMine && canWrite && !editMode && (
              <button
                onClick={() => { setEditMode(true); setEditText(comment.message); }}
                className="text-xs text-gray-400 hover:text-blue-600 font-medium transition"
              >
                ✎ Edit
              </button>
            )}

            {/* Delete — sender only, task open */}
            {isMine && canWrite && (
              confirmDelete ? (
                <span className="flex items-center gap-1.5 text-xs">
                  <span className="text-red-500 font-medium">Delete?</span>
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="text-red-600 hover:text-red-800 font-semibold transition disabled:opacity-50"
                  >
                    {deleting ? '…' : 'Yes'}
                  </button>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="text-gray-400 hover:text-gray-600 font-semibold transition"
                  >
                    No
                  </button>
                </span>
              ) : (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="text-xs text-gray-400 hover:text-red-500 font-medium transition"
                >
                  🗑 Delete
                </button>
              )
            )}
          </div>
        )}

        {/* Inline reply input */}
        {showReplyBox && canWrite && (
          <div className="mt-2 rounded-xl border border-gray-200 overflow-hidden">
            <CommentInput
              onSend={handleSendReply}
              sending={sendingReply}
              members={members}
              subtasks={[]}
              placeholder={`Reply to ${comment.author_name || 'this comment'}…`}
              replyMode
            />
          </div>
        )}

        {/* Nested replies */}
        {replies.length > 0 && (
          <div className="mt-1 border-l-2 border-gray-100 pl-0">
            {replies.map(reply => (
              <CommentItem
                key={reply.id}
                comment={reply}
                currentUserId={currentUserId}
                taskReadOnly={taskReadOnly}
                members={members}
                subtasks={subtasks}
                searchQuery={searchQuery}
                onReply={onReply}
                onEdit={onEdit}
                onDelete={onDelete}
                depth={1}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
